import useSWR, { useSWRConfig, type SWRConfiguration } from 'swr';
import { useCallback, useMemo } from 'react';
import { useActiveWorkspaceScopeKey } from '@/lib/active-workspace';
import { swrFetcher } from '@/lib/swr-fetcher';
import {
  applySystemLastUpdatedOverride,
  clearSystemLastUpdatedOverride,
  touchSystemLastUpdatedOverride,
} from '@/lib/system-last-updated-overrides';
import type { ApiResponse } from '../model';
import {
  ApiResponseSchema,
  OauthInfo,
  OauthInfoSchema,
  SystemPayload,
  SystemPayloadSchema,
  SystemUpdatePayload,
  SystemUpdatePayloadSchema,
  SystemRecord,
  SystemRecordSchema,
} from '../schemas/system-schemas';
import {
  getSystemDetailUrl,
  matchesSystemsCacheKey,
  revalidateSystemsCache,
  SYSTEMS_LIST_URL,
} from '../system-cache';
import {
  createSystem as createSystemRequest,
  deleteSystem as deleteSystemRequest,
  switchSystemStatus as switchSystemStatusRequest,
  updateSystem as updateSystemRequest,
} from '../system-service';
import type { SystemLifecycleStatus } from '../system-service';

function useWorkspaceScopeKey() {
  return useActiveWorkspaceScopeKey();
}

function patchSystemResponse(
  response: ApiResponse<SystemRecord[]> | undefined,
  systemId: string,
  updater: (system: SystemRecord) => SystemRecord
) {
  if (!response?.data?.length) return response;

  let changed = false;
  const data = response.data.map(system => {
    if (system.system_id !== systemId) return system;
    changed = true;
    return updater(system);
  });

  return changed ? { ...response, data } : response;
}

// Fetch all systems
export function useSystems(params?: {
  authFilter?: string;
  search?: string;
  statusFilter?: string;
  /** Active/Inactive lifecycle (status_filter keeps matching the setup status). */
  lifecycleFilter?: string;
  sortField?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
  useQuery?: boolean;
  /** When false the SWR fetch is skipped entirely (key set to null). Defaults to true. */
  enabled?: boolean;
  swr?: SWRConfiguration<ApiResponse<SystemRecord[]>>;
}) {
  const workspaceScope = useWorkspaceScopeKey();

  const useQuery = Boolean(params?.useQuery);
  const sortField = params?.sortField;
  const sortOrder = params?.sortOrder || 'desc';
  const direction = sortOrder === 'asc' ? 1 : -1;

  const queryParams = new URLSearchParams();
  if (!useQuery) {
    if (params?.search?.trim()) {
      queryParams.set('search', params.search.trim());
    }
    if (params?.authFilter && params.authFilter !== 'all') {
      queryParams.set('auth_filter', params.authFilter);
    }
    if (params?.statusFilter && params.statusFilter !== 'all') {
      queryParams.set('status_filter', params.statusFilter);
    }
    if (params?.lifecycleFilter && params.lifecycleFilter !== 'all') {
      queryParams.set('lifecycle_filter', params.lifecycleFilter);
    }
    if (sortField) {
      queryParams.set('sort_field', sortField);
      queryParams.set('sort_order', sortOrder);
    }
    if (params?.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.set('offset', params.offset.toString());
    }
  }

  const url = `${SYSTEMS_LIST_URL}${
    !useQuery && queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;
  const paramsKey = JSON.stringify({
    useQuery,
    search: params?.search?.trim() || null,
    authFilter: params?.authFilter ?? null,
    statusFilter: params?.statusFilter ?? null,
    lifecycleFilter: params?.lifecycleFilter ?? null,
    sortField: sortField ?? null,
    sortOrder,
    limit: params?.limit ?? null,
    offset: params?.offset ?? null,
  });

  const enabled = params?.enabled !== false;
  const { data, error, isLoading, isValidating, mutate } = useSWR<ApiResponse<SystemRecord[]>>(
    !enabled || workspaceScope === 'pending' ? null : [url, workspaceScope, paramsKey],
    ([url]) =>
      useQuery
        ? swrFetcher(url, ApiResponseSchema(SystemRecordSchema), {
            method: 'QUERY',
            body: JSON.stringify({
              ...(params?.search?.trim() ? { search: params.search.trim() } : {}),
              ...(params?.statusFilter && params.statusFilter !== 'all'
                ? {
                    filter: {
                      status: params.statusFilter,
                    },
                  }
                : {}),
              ...(typeof params?.limit === 'number' ? { limit: params.limit } : {}),
              ...(typeof params?.offset === 'number' ? { offset: params.offset } : {}),
              ...(sortField
                ? {
                    sort_field: {
                      [sortField]: direction,
                    },
                  }
                : {}),
            }),
          })
        : swrFetcher(url, ApiResponseSchema(SystemRecordSchema)),
    params?.swr
  );

  return {
    systems: data?.data,
    total: data?.total,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Fetch single system by ID
export function useSystem(systemId: string | null, options?: { enabled?: boolean }) {
  const workspaceScope = useWorkspaceScopeKey();
  const { data, error, isLoading } = useSWR<ApiResponse<SystemRecord[]>>(
    options?.enabled !== false && systemId && workspaceScope !== 'pending'
      ? [`${SYSTEMS_LIST_URL}?system_id=${encodeURIComponent(systemId)}`, workspaceScope]
      : null,
    ([url]) => swrFetcher(url, ApiResponseSchema(SystemRecordSchema))
  );

  return {
    system: data?.data?.[0] ? applySystemLastUpdatedOverride(data.data[0]) : undefined,
    isLoading,
    error,
  };
}

export function useResolvedSystem(systemId: string | null, options?: { enabled?: boolean }) {
  const { system, isLoading: isSystemLoading, error: systemError } = useSystem(systemId, options);
  const {
    systems,
    isLoading: areSystemsLoading,
    error: systemsError,
  } = useSystems({ enabled: options?.enabled });

  const resolvedSystem = useMemo(() => {
    if (system) return system;
    if (!systemId) return undefined;
    return systems?.find(candidate => candidate.system_id === systemId);
  }, [system, systemId, systems]);

  return {
    system: resolvedSystem,
    isLoading: Boolean(systemId) && (isSystemLoading || (!resolvedSystem && areSystemsLoading)),
    error: systemError ?? systemsError,
  };
}

// Fetch OAuth2 info for a system
export function useOauthInfo(systemId: string | null) {
  const workspaceScope = useWorkspaceScopeKey();
  const { data, error, isLoading } = useSWR<ApiResponse<OauthInfo[]>>(
    systemId && workspaceScope !== 'pending'
      ? [
          `/api/ai-transform/v1/oauth2-info?system_id=${encodeURIComponent(systemId)}`,
          workspaceScope,
        ]
      : null,
    ([url]) => swrFetcher(url, ApiResponseSchema(OauthInfoSchema))
  );

  return {
    oauthInfo: data?.data?.[0] || null,
    isLoading,
    error,
  };
}

// Hook for creating a system
export function useCreateSystem() {
  const createSystem = useCallback(async (payload: SystemPayload): Promise<SystemRecord> => {
    const validatedPayload = SystemPayloadSchema.parse(payload);
    return createSystemRequest(validatedPayload);
  }, []);

  return createSystem;
}

// Hook for updating a system
export function useUpdateSystem() {
  const { mutate } = useSWRConfig();

  const updateSystem = useCallback(
    async (
      systemId: string,
      updates: SystemUpdatePayload,
    ): Promise<SystemRecord> => {
      const validatedUpdates = SystemUpdatePayloadSchema.parse(updates);
      const updatedSystem = await updateSystemRequest(
        systemId,
        validatedUpdates,
      );
      const touchedAt = touchSystemLastUpdatedOverride(systemId);
      const touchedSystem: SystemRecord = {
        ...updatedSystem,
        last_updated: touchedAt,
        last_updated_time: touchedAt,
      };
      const specificSystemKey = getSystemDetailUrl(systemId);

      mutate<ApiResponse<SystemRecord[]>>(
        key => matchesSystemsCacheKey(key, SYSTEMS_LIST_URL),
        current =>
          patchSystemResponse(current, systemId, system => ({
            ...system,
            ...touchedSystem,
          })),
        {
          revalidate: false,
        }
      );

      mutate<ApiResponse<SystemRecord[]>>(
        key => matchesSystemsCacheKey(key, specificSystemKey),
        current =>
          patchSystemResponse(current, systemId, system => ({
            ...system,
            ...touchedSystem,
          })),
        {
          revalidate: false,
        }
      );

      return touchedSystem;
    },
    [mutate]
  );

  return updateSystem;
}

// Toggle the persisted connector lifecycle while keeping all query/list/detail
// caches in sync immediately, just like the MCP lifecycle control.
export function useSetSystemStatus() {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (
      systemId: string,
      status: Extract<SystemLifecycleStatus, "active" | "inactive">,
    ): Promise<SystemRecord> => {
      const updatedSystem = await switchSystemStatusRequest(systemId, status);
      const touchedAt = touchSystemLastUpdatedOverride(systemId);
      const touchedSystem: SystemRecord = {
        ...updatedSystem,
        // Status is the requested lifecycle value. Prefer it immediately even
        // if an intermediary returns a stale representation of the row.
        status,
        last_updated: touchedAt,
        last_updated_time: touchedAt,
      };
      const specificSystemKey = getSystemDetailUrl(systemId);

      await Promise.all([
        mutate<ApiResponse<SystemRecord[]>>(
          (key) => matchesSystemsCacheKey(key, SYSTEMS_LIST_URL),
          (current) =>
            patchSystemResponse(current, systemId, (system) => ({
              ...system,
              ...touchedSystem,
            })),
          { revalidate: false },
        ),
        mutate<ApiResponse<SystemRecord[]>>(
          (key) => matchesSystemsCacheKey(key, specificSystemKey),
          (current) =>
            patchSystemResponse(current, systemId, (system) => ({
              ...system,
              ...touchedSystem,
            })),
          { revalidate: false },
        ),
      ]);
      // The patch shows the new lifecycle at once; refetching every list (as a
      // delete does) re-applies status filters, sort order and counts to it.
      void revalidateSystemsCache(systemId).catch(() => undefined);

      return touchedSystem;
    },
    [mutate],
  );
}

// Hook for deleting a system
export function useDeleteSystem() {
  const { mutate } = useSWRConfig();

  const deleteSystem = useCallback(
    async (systemId: string): Promise<boolean> => {
      const deleted = await deleteSystemRequest(systemId);
      if (deleted) {
        clearSystemLastUpdatedOverride(systemId);
        mutate(key => matchesSystemsCacheKey(key, SYSTEMS_LIST_URL));
      }

      return deleted;
    },
    [mutate]
  );

  return deleteSystem;
}
